import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { openCertificate } from '@/lib/certificates';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatDate, formatTime } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, GraduationCap, Bookmark, CheckCircle2,
  Compass, Send, Clock, ExternalLink, XCircle,
  Award, Download, Sparkles, Eye, CalendarDays, Lightbulb, AlertCircle,
  ChevronDown, ChevronRight, Briefcase,
} from 'lucide-react';
import { journeyStage, stageRank, STAGE_LABEL, STAGE_TONE } from '@/lib/internshipJourney';
import { toast } from 'sonner';
import { isUpcoming, statusLabel, statusTone , todayLocal, isWorkplaceMode, modeLabelFor } from '@/lib/experienceStatus';
import {
  ReflectionDialog, ReflectionAction, CareerDiscoverySection, reflectionKey,
} from '@/pages/parent/CareerReflection';

function dateLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const now = new Date(); now.setHours(0,0,0,0);
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const LIVE_STATUSES = ['requested', 'pending_coordinator_action', 'waitlisted', 'awaiting_reschedule',
  'pending_parent_confirmation', 'confirmed', 'scheduled', 'awaiting_feedback'];

async function downloadCertificate(b) {
  await openCertificate(b.certificate_url, `certificate-${b.career_name?.replace(/\s+/g, '-')}.pdf`);
}

/** Unscheduled first — they have no date yet and are the ones to chase — then newest date first. */
function byNewest(a, b) {
  if (!a.assigned_date && !b.assigned_date) return 0;
  if (!a.assigned_date) return -1;
  if (!b.assigned_date) return 1;
  return b.assigned_date.localeCompare(a.assigned_date);
}

function ExperienceRow({ b, open, onToggle, onConfirm, onDecline, navigate, reflection, onReflect }) {
  const mode = b.assigned_mode || b.requested_mode || 'online';
  const isVisit = isWorkplaceMode(mode);
  const needsParent = b.status === 'pending_parent_confirmation';
  const canReflect = b.status === 'completed' && !!onReflect;

  return (
    <div className={`ce-card overflow-hidden ${needsParent ? 'border-[#F97D6A]/40' : ''}`} data-testid={`experience-${b.id}`}>
      <button onClick={onToggle} className="w-full text-left px-4 py-3 hover:bg-gray-50/60 transition-colors" data-testid={`experience-toggle-${b.id}`}>
        <div className="flex items-center gap-3">
          <div className={`w-1.5 h-9 rounded-full shrink-0 ${needsParent ? 'bg-[#F97D6A]' : b.status === 'confirmed' ? 'bg-emerald-400' : b.status === 'completed' ? 'bg-teal-400' : ['cancelled', 'rejected', 'not_conducted'].includes(b.status) ? 'bg-red-300' : b.status === 'awaiting_feedback' ? 'bg-amber-400' : 'bg-blue-400'}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1F2937] truncate">{b.career_name}</p>
            <p className="text-[11px] text-[#9CA3AF]">
              {modeLabelFor(mode)}
              {b.assigned_date ? ` · ${formatDate(b.assigned_date)}` : ' · date to be arranged'}
              {b.assigned_time_slot ? ` · ${formatTime(b.assigned_time_slot)}` : ''}
              {b.mentor_name ? ` · ${b.mentor_name}` : ''}
            </p>
          </div>
          <Badge className={`text-[9px] font-semibold px-2 py-0.5 rounded-full border-0 shrink-0 ${statusTone(b)}`} data-testid={`experience-status-${b.id}`}>
            {statusLabel(b)}
          </Badge>
          {open ? <ChevronDown className="h-4 w-4 text-[#9CA3AF] shrink-0" /> : <ChevronRight className="h-4 w-4 text-[#C8CDD6] shrink-0" />}
        </div>
      </button>

      {open && (
        <div className="bg-[#FAFBFC] border-t border-gray-100 px-4 py-3 space-y-3" data-testid={`experience-detail-${b.id}`}>
          {b.mentor_name && (
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#FFF0ED] flex items-center justify-center shrink-0 text-sm font-bold text-[#F97D6A]">
                {b.mentor_name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#1F2937]">{b.mentor_name}</p>
                {b.mentor_profession_title && (
                  <p className="text-[11px] text-[#6B7280]">{b.mentor_profession_title}{b.mentor_company_name ? ` at ${b.mentor_company_name}` : ''}</p>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[#4B5563]">
            <span><span className="text-[#9CA3AF]">Date: </span>{b.assigned_date ? formatDate(b.assigned_date) : 'being arranged'}</span>
            <span><span className="text-[#9CA3AF]">Time: </span>{b.assigned_time_slot ? formatTime(b.assigned_time_slot) : '—'}</span>
          </div>

          {isVisit ? (
            b.assigned_venue?.workplace_name && (
              <div className="text-[11px] text-[#4B5563]" data-testid={`venue-${b.id}`}>
                <span className="text-[#9CA3AF]">Workplace: </span>{b.assigned_venue.workplace_name}
                {b.assigned_venue.address ? `, ${b.assigned_venue.address}` : ''}
                {b.assigned_venue.city ? `, ${b.assigned_venue.city}` : ''}
                <p className="text-[11px] text-amber-700 mt-1.5 flex items-start gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                  An approved mentor or workplace host supervises students throughout this workplace visit.
                  Parents handle drop-off and pick-up unless the school arranges transport.
                </p>
              </div>
            )
          ) : (b.google_meet_link || b.session_link) && (
            <a href={b.google_meet_link || b.session_link} target="_blank" rel="noopener noreferrer"
              className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`join-link-${b.id}`}>
              <ExternalLink className="h-3 w-3" /> Joining link
            </a>
          )}

          {b.feedback_rating && (
            <p className="text-[11px] text-[#4B5563]" data-testid={`rating-${b.id}`}>
              <span className="text-[#9CA3AF]">Your rating: </span>{b.feedback_rating} / 5
              {b.feedback_comment ? ` — “${b.feedback_comment}”` : ''}
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {needsParent && (
              <>
                <Button size="sm" onClick={() => onConfirm(b.id)} className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg h-8 text-xs px-4 font-semibold" data-testid={`confirm-${b.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Accept
                </Button>
                <Button size="sm" variant="outline" onClick={() => onDecline(b.id)} className="border-red-200 text-red-600 hover:bg-red-50 rounded-lg h-8 text-xs px-4" data-testid={`decline-${b.id}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1.5" /> Decline
                </Button>
              </>
            )}
            {b.status === 'completed' && b.certificate_url && (
              <Button size="sm" variant="outline" onClick={() => downloadCertificate(b)} className="rounded-lg h-8 text-xs px-3 border-gray-200" data-testid={`row-cert-${b.id}`}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Certificate
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => navigate(`/sessions/${b.id}`)} className="rounded-lg h-8 text-xs px-3 border-gray-200" data-testid={`open-${b.id}`}>
              {b.status === 'awaiting_feedback' ? 'Leave feedback' : 'Open full details'}
            </Button>
            {canReflect && (
              <ReflectionAction existing={reflection} testId={`reflect-${b.id}`}
                onOpen={() => onReflect({
                  type: isVisit ? 'workplace_visit' : 'online_session',
                  id: b.id,
                  careerName: b.career_name,
                  label: modeLabelFor(mode),
                  date: b.assigned_date ? formatDate(b.assigned_date) : '',
                })} />
            )}
          </div>
          {canReflect && reflection?.answers?.explore_further && (
            <p className="text-[11px] text-[#6B7280]" data-testid={`reflection-note-${b.id}`}>
              <span className="text-[#9CA3AF]">Wants to explore further: </span>
              {reflection.answers.explore_further}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ExperienceList({ bookings, navigate, onConfirm, onDecline, reflections, onReflect }) {
  const [openId, setOpenId] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const live = bookings.filter(b => LIVE_STATUSES.includes(b.status)).sort(byNewest);
  const past = bookings.filter(b => !LIVE_STATUSES.includes(b.status)).sort(byNewest);
  const toggle = (id) => setOpenId(openId === id ? null : id);
  const rowProps = { open: null, onConfirm, onDecline, navigate, onReflect };
  const reflectionFor = (b) => reflections?.[reflectionKey(
    isWorkplaceMode(b.assigned_mode || b.requested_mode || 'online')
      ? 'workplace_visit' : 'online_session', b.id)];

  return (
    <div data-testid="experiences-section">
      <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-2.5 flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-[#F97D6A]" /> Experiences
      </h2>
      {live.length === 0 ? (
        <div className="ce-card p-6 text-center" data-testid="empty-experiences">
          <CalendarDays className="h-6 w-6 text-gray-200 mx-auto mb-2" />
          <p className="text-xs text-[#9CA3AF] mb-2">No experiences in progress. Explore a career to request one.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/careers')} className="text-xs rounded-lg h-7 px-3 border-gray-200" data-testid="empty-experiences-cta">
            Explore Careers
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {live.map(b => (
            <ExperienceRow key={b.id} {...rowProps} b={b} open={openId === b.id} onToggle={() => toggle(b.id)} reflection={reflectionFor(b)} />
          ))}
        </div>
      )}

      {past.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowPast(!showPast)} className="text-[11px] font-semibold text-[#6B7280] hover:text-[#1F2937] transition-colors" data-testid="toggle-past">
            {showPast ? 'Hide past experiences' : `Show past experiences (${past.length})`}
          </button>
          {showPast && (
            <div className="space-y-2 mt-2" data-testid="past-experiences">
              {past.map(b => (
                <ExperienceRow key={b.id} {...rowProps} b={b} open={openId === b.id} onToggle={() => toggle(b.id)} reflection={reflectionFor(b)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Internships: part of the child's career portfolio ──

function InternshipsSection({ rows, childId, navigate, reflections, onReflect }) {
  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };
  const ordered = [...rows].sort((a, b) => {
    const rank = (r) => stageRank(journeyStage({
      application: r.application, placement: r.placement,
      program: { applications_open: r.application?.editable }, eligible: true,
    }));
    return rank(a) - rank(b);
  });

  return (
    <div className="ce-card p-4 mt-6" data-testid="child-internships-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-[#B0B5C0]" /> Internships
        </h3>
        <span className="text-[11px] text-[#9CA3AF]">{ordered.length} on record</span>
      </div>
      {ordered.length === 0 && (
        <p className="text-xs text-[#9CA3AF]" data-testid="child-internships-empty">
          No internship applications yet.{' '}
          <button onClick={() => navigate(`/parent/internships?child=${childId}`)} className="text-[#F97D6A] font-semibold hover:underline">
            See what is open
          </button>
        </p>
      )}
      <div className="space-y-2.5">
        {ordered.map(({ application: a, placement: p }) => {
          const stage = journeyStage({
            application: a, placement: p,
            program: { applications_open: a?.editable }, eligible: true,
          });
          return (
            <div key={a.id} className="rounded-xl border border-[#EFF0F2] px-4 py-3" data-testid={`child-internship-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#1F2937] truncate">
                    {p ? p.title : a.program_name}
                  </p>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">
                    {p ? `${p.company_name} · ${a.program_name}` : 'Application submitted to the school'}
                  </p>
                  {p && (
                    <p className="text-sm text-[#6B7280] mt-1">
                      {fmt(p.start_date)} – {fmt(p.end_date)}
                      {p.duration_days ? ` · ${p.duration_days} day${p.duration_days === 1 ? '' : 's'}` : ''}
                      {p.attendance?.marked ? ` · attendance ${p.attendance.summary}` : ''}
                    </p>
                  )}
                  {(p?.career_name || a.priority1?.career_name) && (
                    <p className="text-xs text-[#9CA3AF] mt-1" data-testid={`child-internship-career-${a.id}`}>
                      Career:{' '}
                      {(p?.career_slug || a.priority1?.career_slug) ? (
                        <a href={`/careers/${p?.career_slug || a.priority1.career_slug}`} target="_blank" rel="noreferrer" className="font-semibold text-[#F97D6A] hover:underline">
                          {p?.career_name || a.priority1.career_name}
                        </a>
                      ) : <span className="font-semibold text-[#6B7280]">{p?.career_name || a.priority1.career_name}</span>}
                    </p>
                  )}
                  {!p && a.priority1 && (
                    <p className="text-sm text-[#6B7280] mt-1">
                      Preference 1: {a.priority1.company_name}
                      {a.priority2 ? ` · Preference 2: ${a.priority2.company_name}` : ''}
                    </p>
                  )}
                </div>
                <Badge className={`border-0 text-[10px] ${STAGE_TONE[stage] || 'bg-gray-100 text-gray-500'}`} data-testid={`child-internship-stage-${a.id}`}>
                  {STAGE_LABEL[stage]}
                </Badge>
              </div>
              <button
                onClick={() => navigate(`/parent/internships?child=${childId}&program=${a.program_id}&placement=${a.id}`)}
                className="text-[11px] font-semibold text-[#F97D6A] hover:underline mt-2 inline-flex items-center gap-1"
                data-testid={`view-internship-${a.id}`}
              >
                View internship details <ChevronRight className="h-3.5 w-3.5" />
              </button>
              {onReflect && ['completed', 'partially_completed'].includes(p?.completion?.school_status) && (
                <div className="mt-2">
                  <ReflectionAction
                    existing={reflections?.[reflectionKey('internship', a.id)]}
                    testId={`reflect-internship-${a.id}`}
                    onOpen={() => onReflect({
                      type: 'internship', id: a.id,
                      careerName: p?.career_name || a.priority1?.career_name || 'this internship',
                      label: 'Internship', date: fmt(p?.end_date),
                    })}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Right Sidebar: Certificates ──

function CertificatesSection({ bookings }) {
  const certBookings = bookings
    .filter(b => b.status === 'completed' && b.certificate_url)
    .sort((a, b) => (b.assigned_date || '').localeCompare(a.assigned_date || ''));
  const handleDownload = downloadCertificate;

  return (
    <div className="ce-card p-4" data-testid="certificates-section">
      <div className="flex items-center gap-2 mb-3">
        <Award className="h-4 w-4 text-[#F97D6A]" />
        <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']">Certificates</h3>
      </div>
      {certBookings.length === 0 ? (
        <p className="text-xs text-[#9CA3AF]" data-testid="empty-certificates">
          Certificates appear here once an experience is completed.
        </p>
      ) : (
      <div className="space-y-2">
        {certBookings.map(b => (
          <div key={b.id} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-[#F8F9FA]" data-testid={`cert-${b.id}`}>
            <div className="w-8 h-8 rounded-lg bg-[#FFF0ED] flex items-center justify-center shrink-0">
              <Award className="h-4 w-4 text-[#F97D6A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#1F2937] truncate">{b.career_name}</p>
              <p className="text-[10px] text-[#9CA3AF]">{formatDate(b.assigned_date)}{b.mentor_name ? ` · ${b.mentor_name}` : ''}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => handleDownload(b)} className="rounded-lg h-7 text-[10px] px-2 border-gray-200" data-testid={`download-cert-${b.id}`}>
              <Download className="h-3 w-3 mr-0.5" /> PDF
            </Button>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

// ── Insights ──

function InsightsSection({ bookings, savedCareers, buddyResults, child }) {
  const insights = useMemo(() => {
    const list = [];

    // Insight from saved careers
    if (savedCareers.length > 0) {
      const categories = [...new Set(savedCareers.map(c => c.category).filter(Boolean))];
      if (categories.length > 0) {
        list.push(`Explored careers in ${categories.slice(0, 2).join(' and ')}`);
      }
    }

    // Insight from Career Buddy answers
    if (buddyResults.length > 0) {
      const latest = buddyResults[0];
      const answers = latest.answers || {};
      if (answers.journey && answers.journey !== 'Not sure') {
        const journeyMap = {
          'Structured study': 'structured academic paths',
          'Study + practical': 'a mix of study and practical learning',
          'Skill / portfolio': 'skill and portfolio-based paths',
          'Flexible / explore': 'flexible exploration',
        };
        if (journeyMap[answers.journey]) {
          list.push(`Interested in ${journeyMap[answers.journey]}`);
        }
      }
      if (answers.interest) {
        const interestMap = {
          'Helping people': 'helping people and service',
          'Technology': 'technology and innovation',
          'Business': 'business and strategy',
          'Creativity': 'creativity and design',
          'Sports & fitness': 'sports and active work',
          'Science & research': 'science and research',
          'Teaching': 'teaching and mentoring',
          'Travel & hospitality': 'travel and hospitality',
        };
        if (interestMap[answers.interest]) {
          list.push(`Drawn towards ${interestMap[answers.interest]}`);
        }
      }
    }

    // Insight from bookings
    const completedBookings = bookings.filter(b => b.status === 'completed');
    if (completedBookings.length > 0) {
      list.push(`Completed ${completedBookings.length} career session${completedBookings.length > 1 ? 's' : ''}`);
    }

    // Suggestion if limited activity
    if (list.length === 0 && savedCareers.length === 0 && buddyResults.length === 0) {
      return null; // hide section
    }

    if (list.length < 2 && savedCareers.length < 3) {
      list.push('Try exploring more careers to discover new interests');
    }

    return list.slice(0, 3);
  }, [bookings, savedCareers, buddyResults]);

  if (!insights || insights.length === 0) return null;

  return (
    <div className="ce-card p-4" data-testid="insights-section">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']">Insights</h3>
      </div>
      <div className="space-y-2">
        {insights.map((insight, i) => (
          <div key={i} className="flex items-start gap-2 text-sm text-[#4B5563]">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
            <span>{insight}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Journey: a read-only recap; the actions live in the Experiences section ──

function JourneyOverview({ bookings, internships, reflections, onGoToExperiences }) {
  const completed = bookings
    .filter(b => b.status === 'completed')
    .sort((a, b) => (b.assigned_date || '').localeCompare(a.assigned_date || ''));
  const completedInternships = internships.filter(
    ({ placement: p }) => ['completed', 'partially_completed'].includes(p?.completion?.school_status));
  const items = [
    ...completed.map(b => ({
      key: b.id,
      name: b.career_name,
      meta: `${modeLabelFor(b.assigned_mode || b.requested_mode || 'online')}${b.assigned_date ? ` · ${formatDate(b.assigned_date)}` : ''}`,
      reflected: !!reflections[reflectionKey(
        isWorkplaceMode(b.assigned_mode || b.requested_mode || 'online')
          ? 'workplace_visit' : 'online_session', b.id)],
    })),
    ...completedInternships.map(({ application: a, placement: p }) => ({
      key: a.id,
      name: p?.career_name || p?.title || a.program_name,
      meta: 'Internship',
      reflected: !!reflections[reflectionKey('internship', a.id)],
    })),
  ];
  const reflectedCount = items.filter(i => i.reflected).length;

  return (
    <div className="ce-card p-4" data-testid="journey-overview">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-[#B0B5C0]" /> Completed so far
        </h3>
        <button onClick={onGoToExperiences}
                className="text-[11px] font-semibold text-[#F97D6A] hover:underline inline-flex items-center gap-1"
                data-testid="journey-see-experiences">
          All experiences <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[#9CA3AF]" data-testid="journey-overview-empty">
          Nothing completed yet. Finished sessions, visits and internships are summed up here.
        </p>
      ) : (
        <>
          <p className="text-xs text-[#6B7280] mb-2.5" data-testid="journey-overview-counts">
            {items.length} completed · {reflectedCount} with a reflection
          </p>
          <div className="space-y-1.5">
            {items.slice(0, 6).map(i => (
              <div key={i.key} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-[#F8F9FA]"
                   data-testid={`journey-item-${i.key}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-[#1F2937] truncate">{i.name}</p>
                  <p className="text-[10px] text-[#9CA3AF]">{i.meta}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                  i.reflected ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                  {i.reflected ? 'Reflection added' : 'No reflection'}
                </span>
              </div>
            ))}
          </div>
          {items.length > 6 && (
            <p className="text-[11px] text-[#9CA3AF] mt-2">
              and {items.length - 6} more in Experiences
            </p>
          )}
        </>
      )}
    </div>
  );
}

const SECTIONS = [
  { key: 'journey', label: 'Journey', icon: Compass },
  { key: 'experiences', label: 'Experiences', icon: CalendarDays },
  { key: 'careers', label: 'Careers', icon: Bookmark },
];

// ── Main Page ──

export default function ChildJourneyPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedCareers, setSavedCareers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [buddyResults, setBuddyResults] = useState([]);
  const [internships, setInternships] = useState([]);
  const [expandedBuddy, setExpandedBuddy] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [reflectionsOn, setReflectionsOn] = useState(false);
  const [reflections, setReflections] = useState({});
  const [reflectionQuestions, setReflectionQuestions] = useState([]);
  const [discovery, setDiscovery] = useState(null);
  const [reflecting, setReflecting] = useState(null);
  const [section, setSection] = useState('journey');

  useEffect(() => { setSection('journey'); }, [childId]);

  const fetchAll = useCallback(async () => {
    setReflections({});
    setDiscovery(null);
    setReflecting(null);
    try {
      const [cr, scr, br, buddy, apps, places, aiStatus, refl] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/children/${childId}/saved-careers`).catch(() => ({ data: [] })),
        api.get(`/bookings?child_id=${childId}`),
        api.get(`/career-buddy/results/${childId}`).catch(() => ({ data: [] })),
        api.get('/parent/internship-applications').catch(() => ({ data: [] })),
        api.get('/parent/internship-placements').catch(() => ({ data: [] })),
        api.get(`/buddy-ai/${childId}/status`).catch(() => ({ data: { enabled: false } })),
        api.get(`/children/${childId}/reflections`).catch(() => ({ data: { enabled: false } })),
      ]);
      setChild(cr.data);
      setSavedCareers(scr.data);
      setBookings(br.data);
      setBuddyResults(buddy.data || []);
      setAiEnabled(!!aiStatus.data?.enabled);
      setReflectionsOn(!!refl.data?.enabled);
      setReflectionQuestions(refl.data?.questions || []);
      setDiscovery(refl.data?.discovery || null);
      setReflections(Object.fromEntries(
        (refl.data?.reflections || []).map(r => [reflectionKey(r.experience_type, r.experience_id), r])));
      const mine = (apps.data || []).filter(a => a.child_id === childId);
      setInternships(mine.map(a => ({
        application: a,
        placement: (places.data || []).find(p => p.id === a.id) || null,
      })));
    } catch (err) {
      if (err.response?.status === 403) toast.error('Access denied');
      else toast.error('Failed to load');
      navigate('/parent');
    } finally { setLoading(false); }
  }, [childId, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleConfirm = async (id) => {
    try { await api.put(`/bookings/${id}/confirm`); toast.success('Session confirmed!'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDecline = async (id) => {
    try { await api.put(`/bookings/${id}/decline`); toast.success('Session declined'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return (
    <div>
      <div className="animate-pulse">
        <div className="h-6 w-32 bg-gray-200 rounded mb-5" />
        <div className="bg-white rounded-2xl border p-5 mb-6"><div className="flex gap-4"><div className="w-12 h-12 rounded-2xl bg-gray-100" /><div className="flex-1"><div className="h-5 w-32 bg-gray-100 rounded mb-2" /><div className="h-3 w-20 bg-gray-100 rounded" /></div></div></div>
        <div className="grid lg:grid-cols-3 gap-5"><div className="lg:col-span-2 space-y-4"><div className="bg-white rounded-2xl border p-5 h-40" /><div className="bg-white rounded-2xl border p-5 h-32" /></div><div className="bg-white rounded-2xl border p-5 h-48" /></div>
      </div>
    </div>
  );
  if (!child) return null;


  return (
    <div data-testid="child-journey-page">
      <div>
        <Button variant="ghost" onClick={() => navigate('/parent')} className="text-sm text-[#6B7280] hover:text-[#1F2937] mb-4 -ml-2 rounded-lg" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Home
        </Button>

        {/* Header */}
        <div className="ce-card p-4 sm:p-5 mb-6" data-testid="child-summary">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#FFF0ED] flex items-center justify-center shrink-0 text-xl font-bold text-[#F97D6A]">
              {child.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-extrabold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="child-name">{child.full_name}</h1>
                <Badge className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full border-0 ${child.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : child.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>{child.status}</Badge>
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-[#6B7280]">
                <span className="flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5 text-[#9CA3AF]" />Grade {child.grade}{child.division ? `-${child.division}` : ''}</span>
                {child.admission_number && <span>#{child.admission_number}</span>}
              </div>
              {child.interests?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {child.interests.map((i, j) => <span key={j} className="text-[11px] font-medium bg-[#F5F6F8] text-[#4B5563] px-2 py-0.5 rounded-lg border border-gray-100">{i}</span>)}
                </div>
              )}
            </div>
            <Button onClick={() => navigate('/careers')} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-9 px-4 text-sm font-semibold shrink-0" data-testid="request-btn">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Request Experience
            </Button>
          </div>
        </div>

        {/* Local navigation — three compact sections instead of one long page */}
        <div className="flex items-center gap-1 mb-5 border-b border-gray-200 overflow-x-auto"
             data-testid="child-section-nav">
          {SECTIONS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setSection(key)} data-testid={`child-tab-${key}`}
                    className={`relative px-3 sm:px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                      section === key ? 'text-[#1F2937]' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}>
              <span className="inline-flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5" /> {label}
              </span>
              {section === key && (
                <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-[#F97D6A]" />
              )}
            </button>
          ))}
        </div>

        {section === 'journey' && (
          <div data-testid="section-journey">
            {reflectionsOn && (
              <CareerDiscoverySection discovery={discovery}
                reflectionCount={Object.keys(reflections).length} />
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <div className={`min-w-0 ${aiEnabled ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
                <JourneyOverview bookings={bookings} internships={internships}
                  reflections={reflections}
                  onGoToExperiences={() => setSection('experiences')} />
              </div>
              {aiEnabled && (
                <div className="ce-card p-4 h-fit" data-testid="buddy-ai-entry">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F97D6A] to-[#E85D4A] flex items-center justify-center shrink-0">
                      <Sparkles className="h-4 w-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']">My Career Buddy AI</h3>
                      <p className="text-xs text-[#6B7280] mt-0.5 leading-relaxed">
                        Ask anything about careers, subjects and study paths — answers use {child.full_name?.split(' ')[0]}'s
                        grade, interests and saved careers.
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => navigate(`/parent/children/${childId}/career-buddy-ai`)}
                          className="w-full mt-3 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-9 text-sm font-semibold"
                          data-testid="open-buddy-ai-btn">
                    Open My Career Buddy AI
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {section === 'experiences' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="section-experiences">
            <div className="lg:col-span-2 min-w-0">
              <ExperienceList bookings={bookings} navigate={navigate}
                onConfirm={handleConfirm} onDecline={handleDecline}
                reflections={reflections}
                onReflect={reflectionsOn ? setReflecting : null} />
              <InternshipsSection rows={internships} childId={childId} navigate={navigate}
                reflections={reflections}
                onReflect={reflectionsOn ? setReflecting : null} />
            </div>
            <div className="lg:col-span-1 min-w-0">
              <CertificatesSection bookings={bookings} />
            </div>
          </div>
        )}

        {section === 'careers' && (
          <div className="space-y-6" data-testid="section-careers">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="ce-card p-4" data-testid="saved-careers-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2">
                    <Bookmark className="h-4 w-4 text-[#B0B5C0]" /> Saved Careers
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/careers')} className="text-[11px] text-[#6B7280] hover:bg-gray-50 rounded-lg h-7 px-2">
                    <Compass className="h-3 w-3 mr-1" /> Explore
                  </Button>
                </div>
                {savedCareers.length === 0 ? (
                  <p className="text-xs text-[#9CA3AF]" data-testid="empty-saved">
                    Nothing saved yet. Careers you save while exploring appear here.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {savedCareers.map(c => (
                      <div key={c.slug} onClick={() => navigate(`/careers/${c.slug}`)} className="flex items-center gap-2.5 p-2 rounded-xl bg-[#F8F9FA] hover:bg-gray-100 cursor-pointer transition-colors" data-testid={`saved-${c.slug}`}>
                        {(c.image_card_url || c.image_url) ? (
                          <img src={c.image_card_url || c.image_url} alt={c.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0"><Compass className="h-3.5 w-3.5 text-gray-300" /></div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-[#1F2937] truncate">{c.name}</p>
                          <p className="text-[10px] text-[#9CA3AF]">{c.category}</p>
                        </div>
                        <ExternalLink className="h-3 w-3 text-[#C0C5CC] shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="ce-card p-4" data-testid="career-buddy-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#B0B5C0]" /> Career Matches
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/careers?openBuddy=1')} className="text-[11px] text-[#6B7280] hover:bg-gray-50 rounded-lg h-7 px-2">
                    Try again
                  </Button>
                </div>
                {buddyResults.length === 0 ? (
                  <p className="text-xs text-[#9CA3AF]" data-testid="empty-matches">
                    No matches yet. Career Buddy suggests careers from a short chat.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {buddyResults.slice(0, 4).map((result) => {
                      const isExpanded = expandedBuddy === result.id;
                      const allCareers = result.matched_careers || [];
                      const preview = allCareers.slice(0, 3).map(c => c.name).join(', ');
                      const extra = allCareers.length > 3 ? ` +${allCareers.length - 3} more` : '';
                      return (
                        <div key={result.id} className="rounded-xl bg-[#F8F9FA] overflow-hidden" data-testid={`buddy-result-${result.id}`}>
                          <div className="p-2.5 flex items-center gap-2.5 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => setExpandedBuddy(isExpanded ? null : result.id)}>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] text-[#9CA3AF]">{formatDate(result.created_at)}</p>
                              <p className="text-xs text-[#374151] truncate">{preview}{extra}</p>
                            </div>
                            <span className="text-[11px] text-[#6B7280] shrink-0">{isExpanded ? 'Hide' : 'View'}</span>
                          </div>
                          {isExpanded && (
                            <div className="px-2.5 pb-2.5 space-y-1">
                              {allCareers.map((c, i) => (
                                <div key={c.slug} onClick={() => navigate(`/careers/${c.slug}`)}
                                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white cursor-pointer transition-colors">
                                  <span className="w-5 h-5 rounded-md bg-[#FFF0ED] flex items-center justify-center text-[9px] font-bold text-[#F97D6A] shrink-0">{i + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-[#1F2937] truncate">{c.name}</p>
                                    {c.reasons?.length > 0 && <p className="text-[9px] text-emerald-600 truncate">{c.reasons[0]}</p>}
                                  </div>
                                  <Eye className="h-3 w-3 text-[#C0C5CC] shrink-0" />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {reflecting && (
        <ReflectionDialog
          childId={childId}
          experience={reflecting}
          existing={reflections[reflectionKey(reflecting.type, reflecting.id)]}
          questions={reflectionQuestions}
          onClose={() => setReflecting(null)}
          onSaved={(data) => {
            setReflections((prev) => ({
              ...prev,
              [reflectionKey(reflecting.type, reflecting.id)]: data.reflection,
            }));
            setDiscovery(data.discovery || null);
          }}
        />
      )}
    </div>
  );
}
